import { ChevronRight, LocationOn, Verified } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Typography
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getCandidateApplications } from "app/api/application_api";
import BackButton from "app/components/BackButton";
import CustomDialog from "app/components/CustomDialog";
import Loading from "app/components/MatxLoading";
import ResponseSnackbar from "app/components/ResponseSnackbar";
import { usePostApplication } from "app/hooks/application/usePostApplication";
import useGetCandidate from "app/hooks/candidate/useGetCandidate";
import useGetApplicantCountForJob from "app/hooks/job/useGetApplicantCountForJob";
import useGetJob from "app/hooks/job/useGetJob";
import useAuth from "app/hooks/useAuth";
import { AppRoutes, formatExp } from "app/utils/global_constants";
import {
  buildJobDetailPath,
  isJobPostingIndexable,
  isNonProductionIndexingHost
} from "app/utils/jobSearchSeo";
import DOMPurify from "dompurify";
import { useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import GrayBGText from "./shared/GrayBGText";
import JobCities from "./shared/JobCities";
import SquareImage from "./shared/SquareImage";
import { ApplyButtonStyle, bgWave, LocationOptions } from "./Constants";
import JobBenefitsRequired from "./shared/Job Detail Dialog/JobBenefitsRequired";
import JobSalaryRange from "./shared/Job Detail Dialog/JobSalaryRange";
import JobSkillsRequired from "./shared/Job Detail Dialog/JobSkillsRequired";
import AppliedText from "./shared/Job Detail/AppliedText";
import ApplyDialogContent from "./shared/Job Detail/ApplyDialogContent";
import UpdateProfileButton from "./shared/Job Detail/UpdateProfileButton";
import useNotification from "app/hooks/useNotification";

import { formatSalary, getTimeAgo } from "app/utils/utils";
import AIActionButton from "app/components/AIActionButton";
import useGetSimilarJobs from "app/hooks/job/useGetSimilarJobs";

const LoginToApplyText = ({ onClick = () => {} }) => {
  return (
    <Button
      variant="contained"
      size="small"
      fullWidth
      sx={ApplyButtonStyle}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      Login To Apply!
    </Button>
  );
};

function isSalaryNotDisclosed(minSalary, maxSalary) {
  return Number(minSalary) === 0 && Number(maxSalary) === 0;
}

function getUniqueBenefits(benefits) {
  const uniqueBenefits = new Set(benefits.map((benefit) => benefit.jp_benefit));
  return [...uniqueBenefits];
}

function getApplicantCount(countData) {
  const count = Number(countData?.count);

  if (Number.isNaN(count)) {
    return "0";
  }

  return Number(count) || 0;
}

function formatJobSalary(minSalary, maxSalary) {
  if (isSalaryNotDisclosed(minSalary, maxSalary)) {
    return "Not Disclosed";
  }

  return formatSalary(minSalary, maxSalary);
}

const ActionButtonText = {
  apply: "Apply",
  login: "Login To Apply"
};

function JobDetail() {
  // Job Post Id
  const { id, job } = useParams();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isEnabled = isAuthenticated && id && user?.role === "candidate";
  const queryClient = useQueryClient();
  const shouldDisplayAiScreenBtn =
    isAuthenticated && (user.role === "company" || user.role === "HR");

  const [application, setApplication] = useState();
  const [confirmApply, setConfirmApply] = useState(false);
  const [response, setResponse] = useState({ error: false, success: false, message: "" });
  const { createNotification } = useNotification();
  const [tab, setTab] = useState(0);
  const handleCloseDialog = () => setConfirmApply(false);

  // Try to fetch regular job first
  const {
    data: activeJob,
    isFetching: isLoadingActiveJob,
    isError: isActiveJobError
  } = useGetJob(id, false, Infinity, true);

  const selectedJob = activeJob;
  const isLoadingJobs = isLoadingActiveJob;
  const isDeleted = activeJob?.jp_is_deleted === 1;
  const {
    data: applicantCountData,
    isLoading: isLoadingApplicantCount,
    isError: isApplicantCountError
  } = useGetApplicantCountForJob(selectedJob?.jp_id || id);
  const applicantCount = getApplicantCount(applicantCountData);

  const canonicalPath = selectedJob
    ? buildJobDetailPath(selectedJob.jp_title, selectedJob.jp_id)
    : buildJobDetailPath(job, id);
  const isIndexableJob = isJobPostingIndexable(selectedJob);
  const isNonProductionHost = isNonProductionIndexingHost(window.location.hostname);

  const notification = useMemo(() => {
    return {
      jp_title: `New Application`,
      jp_message: `${user?.name || "A candidate"} has applied for the role of ${selectedJob?.jp_title || "this position"}`,
      jp_status: "pending"
    };
  }, [selectedJob?.jp_title, user?.name]);

  const { data: candidate } = useGetCandidate(user?.id, "");

  const { mutate: apply, isPending: isApplying } = usePostApplication({
    onSuccess: () => {
      handleCloseDialog();
      createNotification(notification, "admin");
      // navigate(0);
      setResponse({ error: false, success: true, message: "Application submitted successfully!" });
      queryClient.invalidateQueries({ queryKey: ["applicationByCandidate"] });
      queryClient.invalidateQueries({ queryKey: ["job_applicant_count", id] });
    },
    onError: (error) => {
      handleCloseDialog();
      setResponse({ error: true, success: false, message: "Failed to submit application." });
    }
  });

  const { data: applicationStatus } = useQuery({
    queryKey: ["applicationByCandidate"],
    queryFn: () => getCandidateApplications(user?.id, id),
    staleTime: Infinity,
    enabled: isEnabled
  });

  const { data: similarJobsData } = useGetSimilarJobs({
    jobId: id,
    limit: 5,
    offset: 0,
    fetchCompanyLogo: true
  });
  const similarJobs = Array.isArray(similarJobsData)
    ? similarJobsData
    : similarJobsData?.rows || similarJobsData?.data || [];

  const [showHiddenBenefits, setShowHiddenBenefits] = useState(false);
  const [showHiddenSkills, setShowHiddenSkills] = useState(false);

  const isCandidate = user?.role === "candidate";
  const isApplied = isCandidate && applicationStatus?.length > 0;
  const isResumeUploaded = candidate?.jp_resume !== null;
  let actionButtonText = "";
  if (isAuthenticated && !isApplied) {
    actionButtonText = ActionButtonText.apply;
  } else {
    actionButtonText = ActionButtonText.login;
  }

  // Button visibility conditions
  const showApplyButton = !isDeleted && !isApplied && isCandidate && isResumeUploaded;
  const showUpdateProfileButton = !isResumeUploaded && isCandidate && !isApplied && !isDeleted;
  const showLoginToApplyButton = !isAuthenticated;
  const showAppliedText = isApplied && isCandidate;

  const jobBenefits = selectedJob?.Benefits ? getUniqueBenefits(selectedJob.Benefits) : [];
  const jobSkills = selectedJob?.jp_required_skills
    ? selectedJob.jp_required_skills.split("| ")
    : [];
  const hiddenSkills = jobSkills.slice(10);
  const hiddenBenefits = jobBenefits.slice(3);
  const sanitizedDescription = selectedJob?.jp_description
    ? DOMPurify.sanitize(selectedJob.jp_description)
    : "";
  const locationType = selectedJob?.jp_location || "";
  const isRemoteWork = locationType.trim() === LocationOptions[1].label.trim(); // Remote/WFH

  const keywords = selectedJob?.jp_keywords ? selectedJob.jp_keywords.split("| ").join(", ") : "";
  const jobType = selectedJob?.jp_type || "";
  const cities = selectedJob?.jp_city ? selectedJob.jp_city.split("| ") : [];
  const countries = selectedJob?.jp_country ? selectedJob.jp_country.split("| ") : [];
  const exp =
    selectedJob?.jp_min_exp && selectedJob?.jp_max_exp
      ? `${formatExp(selectedJob.jp_min_exp)} - ${formatExp(selectedJob.jp_max_exp)} yrs`
      : "Fresher";
  const minSalary = selectedJob?.jp_minimum_salary || "";
  const maxSalary = selectedJob?.jp_maximum_salary || "";
  const aboutCompany = selectedJob?.company?.jp_about_company || "";
  const companyLogo = selectedJob?.company?.jp_logo || null;
  const postedOn = selectedJob ? getTimeAgo(selectedJob.jp_created_at) : null;
  const totalOpenings = selectedJob?.jp_openings_count || 0;

  const isCurrentJobSalaryNotDisclosed = isSalaryNotDisclosed(minSalary, maxSalary);
  const currentJobSalary = isCurrentJobSalaryNotDisclosed
    ? "Not Disclosed"
    : `${minSalary}/year - ${maxSalary}/year`;

  const metaDescription = selectedJob
    ? DOMPurify.sanitize(selectedJob.jp_meta_description || selectedJob.jp_description || "", {
        ALLOWED_TAGS: []
      })
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 160)
    : "View this job opening, requirements, company information, and application details on NextHire.";

  useEffect(() => {
    if (selectedJob && location.pathname !== canonicalPath) {
      navigate(canonicalPath, { replace: true });
    }
  }, [canonicalPath, location.pathname, navigate, selectedJob]);

  useEffect(() => {
    if (!selectedJob || !isIndexableJob || isNonProductionHost) {
      document.getElementById("job-posting-schema")?.remove();
      return undefined;
    }

    window.JobSchemaGenerator?.injectJobPostingSchema(selectedJob, selectedJob.company);
    return () => document.getElementById("job-posting-schema")?.remove();
  }, [isIndexableJob, isNonProductionHost, selectedJob]);

  const isCitiesEmpty = cities.length === 0;

  const onClickActionButton = (candidate, job, user) => {
    if (!isAuthenticated) {
      navigate(AppRoutes.ROUTE_LOGIN_CANDIDATE, { state: { from: location.pathname } });
    } else {
      const application = {
        jp_company_id: job.company.jp_id,
        jp_jobpost_id: job.jp_id,
        jp_candidate_id: user.id,
        jp_status: "pending",
        jp_is_share_to_company: 0,
        mail: {
          jp_candidate_name: user.name,
          jp_email: user.email,
          jp_job_title: job.jp_title,
          jp_phone: candidate ? candidate.jp_contactno : "Not specified",
          jp_company_name: job ? job.company.jp_company_name : "",
          jp_company_email: job ? job.company.jp_email : undefined
        }
      };

      setConfirmApply(true);
      setApplication(application);
    }
  };

  // Add a message for deleted jobs

  const DeletedJobMessage = () => (
    <Typography
      sx={{
        p: 1,
        backgroundColor: "#ffebee",
        borderRadius: "5px",
        color: "#c62828",
        mb: 2
      }}
    >
      This job posting has been deleted and is no longer active
    </Typography>
  );

  const navigateToCompanyDetailsPage = (id) => {
    navigate(`/company/${id}`);
  };

  const navigateToAiScreening = () => navigate(`/jobs/${id}/ai-screening`);

  return (
    <Box sx={bgWave}>
      <Helmet>
        <title>{selectedJob ? `${selectedJob.jp_title} | NextHire` : `${job} | NextHire`}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={keywords} />
        <meta
          name="robots"
          content={
            isNonProductionHost || isActiveJobError || (selectedJob && !isIndexableJob)
              ? "noindex, follow"
              : "index, follow"
          }
        />
        <meta property="og:title" content={selectedJob?.jp_title || job} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={`https://nexthire.reyan.co.in${canonicalPath}`} />
      </Helmet>
      {response.success && (
        <ResponseSnackbar
          open
          onClose={() => setResponse({ ...response, success: false })}
          severity="success"
          message={"Application submitted successfully!"}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        />
      )}
      {response.error && (
        <ResponseSnackbar
          open
          onClose={() => setResponse({ ...response, error: false })}
          severity="error"
          message={"Failed to post application. Try again later."}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        />
      )}
      {user && (
        <CustomDialog
          open={confirmApply}
          dialogTitle={`Apply To ${selectedJob?.jp_title}`}
          onClose={handleCloseDialog}
          maxWidth={"sm"}
          fullWidth
        >
          <ApplyDialogContent
            handleCloseDialog={handleCloseDialog}
            apply={apply}
            application={application}
            locations={isCitiesEmpty ? countries : cities}
            isApplying={isApplying}
            isRemoteWork={isRemoteWork}
          />
        </CustomDialog>
      )}
      {isLoadingJobs && <Loading />}

      {!isLoadingJobs && selectedJob && (
        <Container
          maxWidth="xl"
          sx={{
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",

            mb: 4
          }}
        >
          <Box pt={2}>
            <BackButton label={"Go Back"} />
          </Box>
          {isDeleted && <DeletedJobMessage />}
          <Grid container justifyContent={"center"} spacing={2}>
            <Grid item xs={12} md={7} lg={9}>
              {/** Job Title and Company Name*/}
              <Box
                width={"100%"}

                // sx={{
                //   position: "relative",
                //   backgroundImage: `url(${bgWaveImage})`,
                //   backgroundSize: "cover",
                //   backgroundPosition: "center",
                //   backgroundRepeat: "no-repeat",
                //   borderRadius: 3,
                //   overflow: "hidden"
                // }}
              >
                <Box
                  display={"flex"}
                  justifyContent={"space-between"}
                  alignItems={"start"}
                  width={"100%"}
                >
                  {/* Job Details */}
                  <Box p={2} width={"100%"}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%"
                      }}
                    >
                      <Typography variant="h4" fontWeight={"bold"}>
                        {selectedJob.jp_title}
                      </Typography>
                      {shouldDisplayAiScreenBtn && (
                        <AIActionButton label={"AI Screening"} onClick={navigateToAiScreening} />
                      )}
                    </Box>
                    <Box display="flex" gap={1} alignItems={"center"}>
                      <Typography
                        variant="subtitle1"
                        fontSize={"1.5em"}
                        sx={{ color: "text.secondary", fontWeight: 300 }}
                      >
                        {selectedJob.company.jp_company_name}
                      </Typography>
                      {selectedJob.company.jp_is_verified ? (
                        <Verified sx={{ color: "green" }} />
                      ) : (
                        ""
                      )}
                    </Box>
                    {/* Cities */}
                    <Box
                      display={"flex"}
                      alignItems={"flex-start"}
                      sx={{ flexBasis: { xs: "100%", md: "auto" } }}
                    >
                      <LocationOn />
                      {/* If cities not mentioned then show countries */}
                      {isCitiesEmpty ? (
                        <JobCities cities={countries} />
                      ) : (
                        <JobCities cities={cities} />
                      )}
                    </Box>
                    <Box display={"flex"} alignItems={"center"} flexWrap={"wrap"} gap={1}>
                      {/* Job Type */}
                      <GrayBGText loc={jobType} />
                      {/* Location Type */}
                      <GrayBGText loc={locationType} />
                      {/* Experience */}
                      <GrayBGText loc={exp} />
                      {/* Apply By */}
                      <GrayBGText
                        loc={`Apply By: ${new Date(selectedJob.jp_end_date).toLocaleDateString()}`}
                      />
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <LabelValueText label={"Posted On"} value={postedOn} />
                      <Divider orientation="vertical" flexItem />
                      <LabelValueText label={"Total Openings"} value={totalOpenings} />
                      <Divider orientation="vertical" flexItem />
                      {!isApplicantCountError && (
                        <LabelValueText
                          label={"Applicants"}
                          value={isLoadingApplicantCount ? "0" : applicantCount}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Paper p={1} sx={{ borderRadius: 3 }}>
                <Tabs
                  value={tab}
                  onChange={(e, newValue) => setTab(newValue)}
                  sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}
                >
                  <Tab
                    label={
                      <span style={{ fontWeight: tab === 0 ? "bold" : "normal" }}>Overview</span>
                    }
                    value={0}
                  />
                  <Tab
                    label={
                      <span style={{ fontWeight: tab === 1 ? "bold" : "normal" }}>Company</span>
                    }
                    value={1}
                  />
                </Tabs>

                {tab === 0 && (
                  <Box sx={{ display: { xs: "block", md: "flex" }, minHeight: "600px", gap: 3 }}>
                    {/* Main Content */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        component={"div"}
                        sx={{ overflow: "auto", px: 2 }}
                        dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
                      />
                      <Box sx={{ p: 2 }}>
                        {isCurrentJobSalaryNotDisclosed ? (
                          <Box mb={2}>
                            <Typography gutterBottom variant="subtitle2" fontWeight={"bold"}>
                              Yearly Salary Range
                            </Typography>
                            <Typography color="gray">Not Disclosed</Typography>
                          </Box>
                        ) : (
                          <JobSalaryRange minSalaryRange={minSalary} maxSalaryRange={maxSalary} />
                        )}
                        <JobBenefitsRequired
                          allBenefits={jobBenefits}
                          showHiddenBenefits={showHiddenBenefits}
                          hiddenBenefits={hiddenBenefits}
                          jobBenefits={jobBenefits.slice(0, 3)}
                          setShowHiddenBenefits={setShowHiddenBenefits}
                        />
                        <Box mb={2}>
                          <JobSkillsRequired
                            showHiddenSkills={showHiddenSkills}
                            hiddenSkills={hiddenSkills}
                            jobSkills={jobSkills}
                            setShowHiddenSkills={setShowHiddenSkills}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}

                {tab === 1 && (
                  <Box minHeight={"600px"} p={2}>
                    <Typography>{aboutCompany}</Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={5} lg={3}>
              {/* Side Panel */}
              <Box
                sx={{
                  position: { md: "sticky" },
                  border: "1px solid #eee",
                  borderRadius: 4,
                  boxShadow: 2,
                  p: 2,
                  background: "#fff",
                  height: "100%"
                }}
              >
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <SquareImage
                    companyLogo={companyLogo}
                    companyName={selectedJob?.company?.jp_company_name}
                    size={48}
                  />
                  <Box>
                    <Box display="flex" alignItems={"flex-start"}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {selectedJob?.company?.jp_company_name}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => navigateToCompanyDetailsPage(selectedJob?.company?.jp_id)}
                      >
                        <ChevronRight fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {isCitiesEmpty ? countries.join(", ") : cities.join(", ")}
                    </Typography>
                  </Box>
                </Box>
                <Box mb={2}>
                  <Typography variant="h6" fontWeight="bold">
                    {selectedJob?.jp_title}
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                    <GrayBGText loc={jobType} />
                    <GrayBGText loc={locationType} />
                    <GrayBGText loc={exp} />
                  </Box>
                  <Box mt={1}>
                    <Typography variant="body2" color="text.secondary">
                      Salary: {currentJobSalary}
                    </Typography>
                  </Box>
                </Box>
                {/* Apply Button logic */}
                {showApplyButton && (
                  <LoadingButton
                    onClick={() => onClickActionButton(candidate, selectedJob, user)}
                    variant="contained"
                    sx={{ width: "100%", mb: 2 }}
                    disabled={!isResumeUploaded}
                  >
                    {actionButtonText}
                  </LoadingButton>
                )}
                {showUpdateProfileButton && (
                  <UpdateProfileButton fullWidth={true} job={selectedJob} />
                )}
                {showLoginToApplyButton && (
                  <LoginToApplyText onClick={() => navigate("/session/login/candidate")} />
                )}
                {showAppliedText && <AppliedText />}
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                    Similar jobs
                  </Typography>
                  <Box
                    sx={{
                      overflowY: "auto",
                      maxHeight: "500px",
                      height: "100%"
                    }}
                  >
                    {similarJobs && similarJobs.length > 0 ? (
                      similarJobs.map((job, idx) => {
                        const experience =
                          job.jp_min_exp && job.jp_max_exp
                            ? `${formatExp(job.jp_min_exp)}-${formatExp(job.jp_max_exp)} yrs`
                            : "Not disclosed";

                        return (
                          <Link
                            key={idx}
                            to={buildJobDetailPath(job.jp_title, job.jp_id)}
                            underline="none"
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                p: 1,
                                mb: 1,
                                borderBottom:
                                  idx !== similarJobs.length - 1 ? "1px solid #eee" : "none",
                                border: "1px solid #f0f0f0",
                                borderRadius: 2,
                                cursor: "pointer",
                                "&:hover": { background: "#f5f7fa" }
                              }}
                            >
                              <SquareImage
                                companyLogo={job?.company?.jp_logo}
                                companyId={job?.company?.jp_id}
                                companyName={job?.company?.jp_company_name}
                                size={32}
                              />
                              <Box>
                                <Typography fontWeight="bold" fontSize={14}>
                                  {job.jp_title}
                                </Typography>
                                <Typography fontSize={12} color="text.secondary">
                                  {job?.company?.jp_company_name}
                                </Typography>
                                <Typography fontSize={12} color="primary.main" fontWeight={600}>
                                  {isSalaryNotDisclosed(
                                    job.jp_minimum_salary,
                                    job.jp_maximum_salary
                                  )
                                    ? "Not Disclosed"
                                    : `₹${formatJobSalary(
                                        job.jp_minimum_salary,
                                        job.jp_maximum_salary
                                      )}`}
                                </Typography>
                              </Box>
                              <Box ml="auto">
                                <Typography fontSize={12} color="text.secondary">
                                  {experience}
                                </Typography>
                              </Box>
                            </Box>
                          </Link>
                        );
                      })
                    ) : (
                      <Box sx={{ p: 2, textAlign: "center", color: "text.secondary" }}>
                        <Typography variant="body2">
                          No similar jobs are currently available.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      )}
    </Box>
  );
}

function LabelValueText({ label, value }) {
  return (
    <Typography variant="body1" fontWeight={500} color="text.secondary">
      {label}:{" "}
      <Typography component="span" variant="body2" fontWeight="bold" color="text.primary">
        {value}
      </Typography>
    </Typography>
  );
}

export default JobDetail;
